import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useTilesetUpload } from '@/context/TilesetUploadContext';
import { getActiveTilesets } from '@/queries/data-uploads/get-active-tilesets';
import { getTilesetStatus } from '@/queries/data-uploads/get-tileset-status';
import { queryClient } from '@/queries/query.client';

// How often to re-check which conversions are still running (ms). The backend
// sweeper refreshes the DB every 30s, so this mostly reads fresh data.
const ACTIVE_REFETCH_MS = 15 * 1000;

/**
 * Resumes tracking of tileset conversions across page loads. Uploads only live
 * as long as their tab, but once the file reaches the tile server the
 * conversion continues server-side — this watcher picks those up when the app
 * opens: it announces how many are still running, forces a fresh status sync
 * from the tile server, and toasts when each one finishes.
 *
 * The conversion the TilesetUploadContext is actively tracking is excluded so
 * its own toasts are not duplicated. Renders nothing.
 */
export function TilesetProcessingWatcher() {
	const { t } = useTranslation();
	const { activeUpload } = useTilesetUpload();
	// Conversions we are watching: id -> display name. Ref, not state — nothing renders.
	const trackedRef = useRef<Map<string, string>>(new Map());
	const primedRef = useRef(false);
	const activeUploadId = activeUpload?.dataUploadId;

	const { data: activeTilesets } = useQuery({
		queryKey: ['data-uploads', 'active-tilesets'],
		queryFn: getActiveTilesets,
		refetchInterval: ACTIVE_REFETCH_MS,
	});

	useEffect(() => {
		if (!activeTilesets) return;

		const currentIds = new Set(activeTilesets.map((upload) => upload.id));

		if (!primedRef.current) {
			// App just opened: announce conversions resumed from earlier sessions and
			// force an authoritative sync so a finished-but-stale one resolves right away.
			primedRef.current = true;
			const resumed = activeTilesets.filter((upload) => upload.id !== activeUploadId);
			if (resumed.length > 0) {
				toast.info(
					t(
						'files.tileset.resumedProcessing',
						resumed.length === 1 ? { count: 1, name: resumed[0].name } : { count: resumed.length },
					),
				);
				resumed.forEach((upload) => {
					getTilesetStatus(upload.id).catch(() => undefined);
				});
			}
			activeTilesets.forEach((upload) => trackedRef.current.set(upload.id, upload.name));
			return;
		}

		// New conversions (other tabs, other users in the org) join the watch list.
		activeTilesets.forEach((upload) => trackedRef.current.set(upload.id, upload.name));

		// A watched conversion no longer active = it reached ready/error (or was
		// deleted). Fetch its final state and notify.
		for (const [id, name] of [...trackedRef.current.entries()]) {
			if (currentIds.has(id)) continue;
			trackedRef.current.delete(id);
			if (id === activeUploadId) continue;
			void (async () => {
				const final = await getTilesetStatus(id).catch(() => null);
				const status = final?.tileset?.status;
				if (status === 'ready') {
					toast.success(t('files.tileset.tilesetReadyToast', { name: final?.name ?? name }));
					// Refresh the list badge and the map's tile loader.
					await queryClient.invalidateQueries({ queryKey: ['data-uploads'] });
				} else if (status === 'error') {
					toast.error(t('files.tileset.tilesetFailedToast', { name: final?.name ?? name }), {
						description: final?.tileset?.errorMessage || undefined,
					});
					await queryClient.invalidateQueries({ queryKey: ['data-uploads', 'list'] });
				}
				// Fetch failed or the upload was deleted: nothing to announce.
			})();
		}
	}, [activeTilesets, activeUploadId, t]);

	return null;
}
